"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { EdgePreview, EdgePreviewRow } from "@/lib/pricing/edge-preview";
import { useEdgePreview } from "@/lib/pricing/use-edge-preview";

function LiveAge({ baseSeconds }: { baseSeconds: number }) {
  const [extra, setExtra] = useState(0);
  useEffect(() => {
    setExtra(0);
    const id = setInterval(() => setExtra((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [baseSeconds]);
  return <>{baseSeconds + extra}s ago</>;
}

function formatAmericanOdds(odds: number | null): string | null {
  if (odds == null) return null;
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatPercent(percent: number): string {
  return `${percent >= 10 ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

function compactSelection(row: EdgePreviewRow): string {
  if (!row.subject) return row.selection;
  const selection = row.selection.trim();
  if (selection.toLowerCase().startsWith(row.subject.toLowerCase())) {
    return selection.slice(row.subject.length).trim();
  }
  return selection;
}

function EdgeTickerItem({ row }: { row: EdgePreviewRow }) {
  const odds = formatAmericanOdds(row.oddsAmerican);
  const selection = compactSelection(row);
  const support =
    row.peerBookCount > 0
      ? `${row.peerBookCount} book${row.peerBookCount === 1 ? "" : "s"}`
      : null;

  return (
    <span
      data-testid="edge-preview-item"
      aria-label={`${row.league ? `${row.league} ` : ""}${row.bookLabel ?? row.book ?? ""} ${row.selection} ${odds ?? ""} ${formatPercent(row.evPercent)} EV`}
      className="mr-3 flex h-[58px] w-[min(78vw,25rem)] shrink-0 items-center gap-3 rounded-md border border-border/70 bg-background/55 px-3 py-2 shadow-[inset_0_0_20px_rgba(0,212,255,0.045)]"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex min-w-0 items-center gap-1.5">
          {row.league && (
            <span className="shrink-0 rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              {row.league}
            </span>
          )}
          {row.bookLabel && (
            <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-primary ring-1 ring-primary/20">
              {row.bookLabel}
            </span>
          )}
          <span className="min-w-0 truncate text-[11px] font-semibold text-foreground/80">
            {selection}
          </span>
          {odds && <span className="shrink-0 text-[11px] font-bold text-foreground">{odds}</span>}
        </span>
        <span className="min-w-0 truncate text-[12px] font-semibold leading-tight text-foreground">
          {row.subject ?? row.eventName ?? row.marketCategory}
          <span className="font-normal text-muted-foreground"> · {row.marketDisplay}</span>
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span className="rounded bg-accent-value/10 px-2 py-0.5 text-[12px] font-bold text-accent-value ring-1 ring-accent-value/20">
          ▲ {formatPercent(row.evPercent)}
        </span>
        {support && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
            {support}
          </span>
        )}
      </span>
    </span>
  );
}

function EdgeRowGroup({ rows, groupKey }: { rows: EdgePreview["rows"]; groupKey: string }) {
  return (
    <span className="flex shrink-0 items-center" aria-hidden="true">
      {rows.map((row, i) => (
        <EdgeTickerItem key={`${groupKey}-${row.id}-${i}`} row={row} />
      ))}
    </span>
  );
}

function EdgePreviewSkeletonItem({ index }: { index: number }) {
  const widths = ["w-24", "w-28", "w-20"];
  const shimmer = "animate-pulse rounded-full bg-gradient-to-r from-white/[0.05] via-primary/15 to-white/[0.05]";

  return (
    <span
      className="mr-3 flex h-[58px] w-[min(78vw,25rem)] shrink-0 items-center gap-3 rounded-md border border-border/70 bg-background/55 px-3 py-2 shadow-[inset_0_0_20px_rgba(0,212,255,0.045)]"
      aria-hidden="true"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="flex items-center gap-1.5">
          <span className={cn(shimmer, "h-4 w-10 rounded")} />
          <span className={cn(shimmer, "h-4 w-16 rounded")} />
          <span className={cn(shimmer, "h-3", widths[index % widths.length])} />
          <span className={cn(shimmer, "h-3 w-10")} />
        </span>
        <span className={cn(shimmer, "h-4 w-[72%] max-w-64")} />
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1.5">
        <span className={cn(shimmer, "h-5 w-16 rounded")} />
        <span className={cn(shimmer, "h-3 w-12")} />
      </span>
    </span>
  );
}

function EdgePreviewSkeleton() {
  return (
    <div data-testid="edge-preview-skeleton" className="flex w-max" aria-label="Loading edge preview ticker">
      {Array.from({ length: 3 }, (_, index) => (
        <EdgePreviewSkeletonItem key={index} index={index} />
      ))}
    </div>
  );
}

function EdgeLiveCue() {
  return (
    <span
      data-testid="edge-preview-live-cue"
      className="pointer-events-none absolute left-3 top-1/2 z-10 inline-flex -translate-y-1/2 items-center gap-1.5 rounded-full border border-accent-value/20 bg-background px-2 py-1 data-num text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-value shadow-[0_0_14px_rgba(109,255,4,0.08)] backdrop-blur"
      aria-hidden="true"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-value opacity-40" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-value" />
      </span>
      LIVE
    </span>
  );
}

function EdgePreviewFallback() {
  return (
    <div
      data-testid="edge-preview-fallback"
      className="flex h-[58px] items-center justify-center px-3 data-num text-[11px] uppercase tracking-[0.16em] text-muted-foreground/70"
    >
      Live edge preview unavailable
    </div>
  );
}

export function EdgeStrip() {
  const edgePreview = useEdgePreview();
  const livePreview = edgePreview.status === "live" ? edgePreview.preview : null;
  const rows = livePreview?.rows ?? [];
  const isLive = livePreview !== null;
  const ariaLabel = livePreview
    ? `${livePreview.liveEdgeCount} current edge previews across ${livePreview.bookCount} books`
    : edgePreview.status === "loading"
      ? "Loading edge preview ticker"
      : "Live edge preview unavailable";

  return (
    <div
      data-testid="edge-preview-ticker"
      className="border-b border-border bg-primary/[0.03]"
      aria-label={ariaLabel}
    >
      <div className="relative flex items-center px-2 py-2 data-num text-[11px]">
        {livePreview && (
          <>
            <EdgeLiveCue />
            <span className="sr-only">
              Updated <LiveAge baseSeconds={livePreview.ageSeconds} />.
            </span>
          </>
        )}
        <div className="pricing-edge-marquee-fade min-w-0 flex-1 overflow-hidden">
          {isLive ? (
            <div
              className="marquee-track flex w-max"
              style={{ animation: "marquee-left 54s linear infinite" }}
            >
              <EdgeRowGroup rows={rows} groupKey="a" />
              <EdgeRowGroup rows={rows} groupKey="b" />
            </div>
          ) : edgePreview.status === "loading" ? (
            <EdgePreviewSkeleton />
          ) : (
            <EdgePreviewFallback />
          )}
        </div>
      </div>
    </div>
  );
}
